import { format } from 'date-fns';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Copy, ExternalLink, FileText, Clock, CheckCircle2, ShieldCheck, PenLine } from 'lucide-react';
import { API_URL, EVENT_LABELS } from './bookingUtils';

const Panel = ({ title, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 shadow-sm" data-testid={testId}>
    <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{title}</h3>
    {children}
  </div>
);

export const ClientLinkPanel = ({ booking }) => (
  <Panel title="Client Link" testId="client-link-panel">
    {booking.public_url ? (
      <>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 truncate font-mono">{booking.public_url}</code>
          <Button size="icon" variant="outline" className="h-8 w-8 shrink-0" onClick={() => { navigator.clipboard.writeText(booking.public_url); toast.success('Link copied'); }} data-testid="copy-public-link-btn">
            <Copy className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="outline" className="h-8 w-8 shrink-0" asChild>
            <a href={booking.public_url} target="_blank" rel="noopener noreferrer" data-testid="open-public-link-btn"><ExternalLink className="w-3.5 h-3.5" /></a>
          </Button>
        </div>
        {booking.token_expiry && <p className="text-xs text-slate-500">Expires {format(new Date(booking.token_expiry), 'MMM d, yyyy')}</p>}
      </>
    ) : (
      <p className="text-sm text-slate-400 italic">No link generated yet. Use “Send Link” once office fields are complete.</p>
    )}
  </Panel>
);

export const DocumentsPanel = ({ uploads = [], files = [], token }) => {
  if (uploads.length === 0 && files.length === 0) return null;
  return (
    <Panel title="Required Documents" testId="documents-panel">
      <ul className="space-y-2">
        {uploads.map(u => {
          const file = files.find(f => f.upload_id === u.upload_id);
          return (
            <li key={u.upload_id} className="flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2 text-slate-700">
                <FileText className={`w-4 h-4 ${file ? 'text-emerald-600' : 'text-slate-300'}`} />
                {u.label}{u.required && <span className="text-red-500">*</span>}
              </span>
              {file ? (
                <a href={`${API_URL}/api/booking-files/${file.file_id}${token ? `?token=${token}` : ''}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 hover:underline truncate max-w-[45%]" data-testid={`document-link-${u.upload_id}`}>
                  {file.original_filename}
                </a>
              ) : (
                <span className="text-xs text-slate-400 italic">Pending</span>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
};

export const DeclarationPanel = ({ declaration, booking }) => {
  if (!declaration?.declaration_text) return null;
  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4" data-testid="declaration-panel">
      <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
        <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
        <h2 className="font-heading text-xl text-slate-900">Declaration</h2>
      </div>
      <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed bg-[#FAFAF9] border border-slate-100 rounded-lg p-4">{declaration.declaration_text}</p>
      <div className="flex flex-wrap gap-4 text-sm">
        {declaration.require_ack_checkbox && (
          <span className={`inline-flex items-center gap-1.5 ${booking.declaration_acknowledged ? 'text-emerald-700' : 'text-slate-400'}`}>
            <CheckCircle2 className="w-4 h-4" /> {booking.declaration_acknowledged ? 'Acknowledged by client' : 'Acknowledgement pending'}
          </span>
        )}
        {declaration.require_signature && (
          <span className={`inline-flex items-center gap-1.5 ${booking.signature_data ? 'text-emerald-700' : 'text-slate-400'}`}>
            <PenLine className="w-4 h-4" /> {booking.signature_data ? 'Signed by client' : 'Signature pending'}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
        <div className="space-y-2" data-testid="client-signature-block">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Client signature</p>
          {booking.signature_data ? <img src={booking.signature_data} alt="Client signature" className="h-16 object-contain border-b border-slate-300" data-testid="client-signature-preview" /> : <div className="h-16 border-b border-dashed border-slate-300 text-xs text-slate-400 italic flex items-end pb-1">Pending</div>}
        </div>
        <div className="space-y-2" data-testid="company-signature-block">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">For the company</p>
          {booking.company_signature ? (
            <>
              <img src={booking.company_signature.signature_data} alt="Company signature" className="h-16 object-contain border-b border-slate-300" data-testid="company-signature-preview" />
              <p className="text-xs text-slate-700 font-medium">{booking.company_signature.signatory_name}{booking.company_signature.signatory_title ? ` · ${booking.company_signature.signatory_title}` : ''}</p>
            </>
          ) : <div className="h-16 border-b border-dashed border-amber-300 text-xs text-amber-700 italic flex items-end pb-1">Pending – awaiting company confirmation</div>}
        </div>
      </div>
    </section>
  );
};

export const TimelinePanel = ({ events = [] }) => (
  <Panel title="Activity" testId="timeline-panel">
    {events.length === 0 ? (
      <p className="text-sm text-slate-400 italic">No activity yet</p>
    ) : (
      <ol className="space-y-3">
        {events.map(ev => (
          <li key={ev.event_id} className="flex gap-3 text-sm">
            <Clock className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-slate-800 font-medium leading-tight">{EVENT_LABELS[ev.event_type] || ev.event_type}</p>
              <p className="text-xs text-slate-500">
                {ev.created_at && format(new Date(ev.created_at), 'MMM d, yyyy h:mm a')}
                {ev.actor_type && ` · ${ev.actor_type}`}
                {ev.details?.count ? ` · ${ev.details.count} field${ev.details.count > 1 ? 's' : ''}` : ''}
              </p>
            </div>
          </li>
        ))}
      </ol>
    )}
  </Panel>
);
